import React from "react";
import Select from "react-select";
import { DateTime } from "luxon";

import { useFormContext, Controller } from "react-hook-form";

import FormItem from "app/components/inputs/FormItem";
import DateSelector from "./DateSelector";

import { errorMessageFor } from "./helpers";
import { useCalendarUtils } from "app/routes/Calendar/hooks/useCalendarUtils";

import { SelectStyles } from "./constants";
import { getRepeatOptions } from "app/models/EventModel";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

type PropsDef = {
  compact?: boolean;
};

export default function RecurrenceSelector({ compact = false }: PropsDef) {
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const { getRecurrenceLabel } = useCalendarUtils();

  const watchRepeat = watch("repeat");
  const watchId = watch("id");
  const startDate = DateTime.fromJSDate(watch("date"));
  const isEdit = !!watchId;

  const onChangeRepeat = (val: any) => {
    setValue("repeat", val.value ?? null);

    if (val) {
      if (startDate.isValid) {
        setValue("untilDate", startDate.plus({ months: 1 }).toJSDate());
      } else {
        setValue("untilDate", DateTime.now().plus({ months: 1 }).toJSDate());
      }
    } else {
      setValue("untilDate", null);
    }
  };

  let maxUntilDate = startDate.plus({ years: 1 });
  if (watchRepeat === "daily") {
    maxUntilDate = startDate.plus({ months: 6 });
  }

  const repeatSelect = (
    <Controller
      control={control}
      name="repeat"
      render={({ field: { value, ref } }) => (
        <Select
          styles={SelectStyles}
          isSearchable={false}
          inputRef={ref}
          options={getRepeatOptions().map((o) =>
            getRecurrenceLabel(startDate, o),
          )}
          value={
            watchRepeat
              ? getRecurrenceLabel(
                  startDate,
                  getRepeatOptions().find((c) => c.value === value),
                )
              : getRepeatOptions()[0]
          }
          onChange={onChangeRepeat}
          isDisabled={isEdit}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          classNamePrefix="rs"
          placeholder={t("Repeat")}
        />
      )}
    />
  );

  // The compact variant used to render the DateSelector bare, with no FormItem and so
  // no error slot at all — and every caller is compact (ScheduleBlockForm, both
  // SessionForm sites). So an until_date rejection from the API reached form state via
  // setError and then had nowhere to show: the coach saw the save fail with no reason,
  // the message visible only in the network tab. Reported on coaching_service#889 for
  // a repeating unavailability block; sessions had the same hole.
  const untilDateField = watchRepeat ? (
    compact ? (
      <FormItem error={errorMessageFor(errors, "untilDate")}>
        <DateSelector
          {...register("untilDate")}
          control={control}
          disabled={!!watchId}
          maxDate={maxUntilDate.toJSDate()}
        />
      </FormItem>
    ) : (
      <FormItem
        className={styles.input_field}
        error={errorMessageFor(errors, "untilDate")}
      >
        <label>{t("Until Date")}</label>
        <DateSelector
          {...register("untilDate")}
          control={control}
          disabled={!!watchId}
          maxDate={maxUntilDate.toJSDate()}
        />
      </FormItem>
    )
  ) : null;

  if (compact) {
    return (
      <>
        <div className={styles.sub_row_item}>
          {repeatSelect}
        </div>
        {untilDateField && (
          <div className={styles.sub_row_item}>
            {untilDateField}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <FormItem className={styles.input_field}>
        <label>{t("Repeat")}</label>
        {repeatSelect}
      </FormItem>

      {untilDateField}
    </>
  );
}
